import React, { FC, useMemo } from 'react'
import ReactPixel from '@bettercart/react-facebook-pixel'
import { STRIPE_PUBLISHABLE_KEY, ENABLE_META_PIXEL } from '../../../constants'
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure' //Using pure version to remove side effects of import (https://github.com/stripe/stripe-js/issues/43)
import { useCreateCheckoutSessionQuery } from '../../../redux/services/skeema/payment.endpoints.ts'
import {
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Heading,
  Spinner,
  IconButton,
  Box,
  Flex,
  Button,
} from '@chakra-ui/react'
import { modalCloseButtonClass } from './PaymentFlowModal'
import { IoMdArrowBack } from 'react-icons/io'
import { useUserContext } from '../../../contexts/UserContext'
import { getProPricing } from '../../../utils/pricingUtils'
import { trackMetaConversion, generateEventId } from '../../../utils/metaConversionsUtils'

interface Props {
  isAnnualPlan: boolean
  onBack: () => void
  isAnnualPlanToggled: boolean
  setIsAnnualPlanToggled: React.Dispatch<React.SetStateAction<boolean>>
}

const CheckoutModalContent: FC<Props> = (props) => {
  const { captureAnalytics, userInfo } = useUserContext()
  const proPricing = userInfo ? getProPricing(userInfo) : undefined

  const { isAnnualPlan, onBack, isAnnualPlanToggled, setIsAnnualPlanToggled } = props
  const { data: checkoutSession } = useCreateCheckoutSessionQuery(
    { is_annual: isAnnualPlan, is_v2: proPricing?.v2 },
    { refetchOnFocus: false },
  )
  const clientSecret = checkoutSession?.client_secret

  //Docs recommend this is loaded outside the component,
  //but we want it here so we don't unnecessarily mount stripe's script
  const stripePromise = useMemo(() => {
    return loadStripe(STRIPE_PUBLISHABLE_KEY)
  }, [])

  const handleClickAnnualPlan = () => {
    setIsAnnualPlanToggled(true)
    captureAnalytics('payment_flow_checkout:pricing_modal_annual_plan_click')
  }
  const handleClickMonthlyPlan = () => {
    setIsAnnualPlanToggled(false)
    captureAnalytics('payment_flow_checkout:pricing_modal_monthly_plan_click')
  }

  return (
    <ModalContent p="32px" borderRadius="16px" width="475px">
      <ModalHeader p="0" mb="16px" alignItems="center">
        <Heading fontSize="16px" fontWeight={600} color="#000" lineHeight="28px">
          <Box alignItems="center" gap={2} display="flex">
            <IconButton
              variant="outline"
              border="none"
              aria-label="Go to billing cycle"
              icon={<IoMdArrowBack />}
              onClick={onBack}
              height="32px"
              width="32px"
              minWidth="32px"
              flexShrink={0}
              _hover={{
                bg: '#EBEBEB',
              }}
            />
            Back
          </Box>
        </Heading>
      </ModalHeader>
      <ModalCloseButton className={modalCloseButtonClass} />

      <ModalBody p="0" overflow="hidden">
        <Heading fontSize="20px" fontWeight={600} color="#000" lineHeight="28px" mb="16px">
          Upgrade to Pro
        </Heading>
        <Text fontSize="16px" mb="16px" color="#000" lineHeight="24px">
          Less than a fancy coffee—for a whole month of mental clarity ☕
        </Text>
        <Flex w="100%" alignItems="center" justifyContent="flex-end" mb="32px">
          <Flex borderRadius="24px" bg="#f6f6f6" p="4px">
            <Button
              bg={!isAnnualPlanToggled ? 'black' : '#f6f6f6'}
              color={!isAnnualPlanToggled ? 'white' : '#585858'}
              borderRadius="100px"
              p="4px 14px"
              height="30px"
              mr="4px"
              fontWeight={500}
              fontSize={'14px'}
              lineHeight={'22px'}
              _hover={{ bg: `${!isAnnualPlanToggled ? 'black' : 'transparent'}` }}
              onClick={handleClickMonthlyPlan}
              autoFocus
            >
              Monthly
            </Button>
            <Button
              bg={isAnnualPlanToggled ? 'black' : '#f6f6f6'}
              color={isAnnualPlanToggled ? 'white' : '#585858'}
              borderRadius="100px"
              p="4px 14px"
              height="30px"
              fontWeight={500}
              fontSize={'14px'}
              lineHeight={'22px'}
              _hover={{ bg: `${isAnnualPlanToggled ? 'black' : 'transparent'}` }}
              onClick={handleClickAnnualPlan}
              autoFocus
            >
              Yearly
              <Text
                ml="6px"
                lineHeight="16px"
                fontSize="12px"
                color={isAnnualPlanToggled ? 'white' : '#0071E3'}
              >
                save {proPricing?.discount}%
              </Text>
            </Button>
          </Flex>
        </Flex>
        {!clientSecret && (
          <div className="flex-center" style={{ width: '100%' }}>
            <Spinner color="blue.500" size="lg" />
          </div>
        )}
        {clientSecret && (
          <div id="checkout" key={clientSecret}>
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{
                clientSecret,
                onComplete: () => {
                  try {
                    if (ENABLE_META_PIXEL) {
                      const eventId = generateEventId()
                      const purchaseValue = isAnnualPlan
                        ? proPricing?.yearlyTotal
                        : proPricing?.monthly
                      // Track server-side conversion first (using Subscribe since Purchase is not allowed)
                      trackMetaConversion({
                        eventName: 'Subscribe',
                        eventId,
                        email: userInfo?.email,
                        customData: {
                          value: purchaseValue,
                          currency: 'USD',
                        },
                      })
                      // Then track client-side pixel with same event ID for deduplication
                      ReactPixel.track(
                        'Purchase',
                        {
                          value: purchaseValue,
                          currency: 'USD',
                        },
                        {
                          eventID: eventId,
                        },
                      )
                    }
                  } catch (err) {
                    console.error('Error tracking purchase event')
                  }
                },
              }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}
      </ModalBody>
    </ModalContent>
  )
}

export default CheckoutModalContent
